import Menu from '@/global/components/MenuV2'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu as MuiMenu,
    Typography,
} from '@mui/material'
import { Link as GlobalLink } from 'global/components/Link'
import { ChevronDownIcon, ExternalLinkIcon, MenuIcon } from 'lucide-react'
import { useState } from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import type { Menu as TMenu } from 'routes/types'

interface IProps {
    itens: TMenu
}

export function MenuResponsivo({ itens }: IProps) {
    const [menuAberto, setMenuAberto] = useState('')
    const [menuMobileAberto, setMenuMobileAberto] = useState('')
    const [isMenuVerticalAberto, setIsMenuVerticalAberto] = useState(false)

    const fecharDrawer = () => {
        setMenuMobileAberto('')
        setIsMenuVerticalAberto(false)
    }

    return [
        <div key="menu-desktop" className="hidden lg:flex w-full items-center">
            {itens?.map(item => (
                <Box key={item.label}>
                    <Button
                        color="inherit"
                        onClick={() => {
                            setMenuAberto(item.label)
                        }}
                        id={'botao_' + item.label}
                    >
                        {item.label}
                    </Button>

                    <MuiMenu
                        open={menuAberto === item.label}
                        onClose={() => setMenuAberto('')}
                        anchorReference="anchorPosition"
                        anchorPosition={{
                            top:
                                (document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().top ?? 0) + 40,
                            left:
                                document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().left ?? 0,
                        }}
                        sx={{
                            '& .MuiPaper-root': {
                                minWidth: document
                                    .getElementById('botao_' + item.label)
                                    ?.getBoundingClientRect().width,
                            },
                        }}
                    >
                        {item.subitens.map(subitem => (
                            <LinkButton
                                key={subitem.label}
                                {...subitem}
                                onClick={() => setMenuAberto('')}
                            />
                        ))}
                    </MuiMenu>
                </Box>
            ))}
        </div>,
        <div key="menu-mobile" className="flex lg:hidden w-full">
            <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ m: 2 }}
                onClick={() => setIsMenuVerticalAberto(true)}
                id="botao_menuVertical"
            >
                <MenuIcon />
            </IconButton>
            <Drawer
                anchor="left"
                open={isMenuVerticalAberto}
                onClose={() => fecharDrawer()}
                PaperProps={{
                    style: {
                        width: 'clamp(300px, 25%, 80vw)',
                        scrollbarGutter: 'stable',
                        height: '100vh',
                        overflowY: 'auto',
                    },
                }}
            >
                <List>
                    <ListItemButton onClick={() => fecharDrawer()}>
                        <ListItemIcon>
                            <MenuIcon />
                        </ListItemIcon>
                        <ListItemText primary="Menu" />
                    </ListItemButton>
                </List>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: 2,
                    }}
                >
                    {itens?.map(item => (
                        <Accordion
                            sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                '&:before': {
                                    display: 'none',
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                    padding: 0,
                                },
                                '&.MuiPaper-root': {
                                    minHeight: 'fit-content',
                                },
                            }}
                            expanded={menuMobileAberto === item.label}
                            onClick={() => {
                                setMenuMobileAberto(
                                    menuMobileAberto === item.label
                                        ? ''
                                        : item.label,
                                )
                            }}
                            key={item.label}
                            id={'botao_' + item.label}
                        >
                            <AccordionSummary expandIcon={<ChevronDownIcon />}>
                                <Typography>{item.label}</Typography>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{ flexDirection: 'column', padding: 0 }}
                            >
                                {item.subitens.map(value => (
                                    <LinkButton
                                        key={value.label}
                                        {...value}
                                        onClick={() => fecharDrawer()}
                                        mobile
                                    />
                                ))}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </Drawer>
        </div>,
    ]
}

function LinkButton({
    onClick,
    mobile,
    ...props
}: TMenu[0]['subitens'][0] & {
    onClick?: () => any
    mobile?: boolean
}) {
    if (mobile)
        return (
            <ListItemButton sx={{ p: 0 }} id={'sub-botao_' + props.label}>
                <GlobalLink
                    onClick={onClick}
                    href={props.redirectTo}
                    sx={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'start',
                        alignItems: 'center',
                        p: 1.5,
                        textDecoration: 'none',
                        color: 'text.primary',
                    }}
                    target={props.newTab ? '_blank' : undefined}
                >
                    <ListItemIcon>{props.icon}</ListItemIcon>
                    <Typography>{props.label}</Typography>
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </GlobalLink>
            </ListItemButton>
        )

    return (
        <ReactRouterLink
            id={'sub-botao_' + props.label}
            to={props.redirectTo}
            target={props.newTab ? '_blank' : undefined}
            rel={props.newTab ? 'noopener noreferrer' : undefined}
        >
            <Menu.Item onClick={onClick}>
                <Typography
                    color="text.primary"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {props.label}
                    {props.newTab && (
                        <div
                            title="Abre em uma nova aba"
                            className="ml-4 flex items-center"
                        >
                            <ExternalLinkIcon className="opacity-50 size-4" />
                        </div>
                    )}
                </Typography>
            </Menu.Item>
        </ReactRouterLink>
    )
}
